import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";

import { API_BASE_URL } from "@/shared/config/env";

/* ═══════════════════════════════════════════════════════════════
   Envelope padrão do backend ASP.NET Core
═══════════════════════════════════════════════════════════════ */
interface ApiResponse<T = unknown> {
  statusCode: number;
  data: T;
  mensagemSucesso: string | null;
  errors: string[] | null;
  idItem: number | null;
}

/* ═══════════════════════════════════════════════════════════════
   Mutex para evitar múltiplos refreshes simultâneos
═══════════════════════════════════════════════════════════════ */
const refreshMutex = new Mutex();

/* ═══════════════════════════════════════════════════════════════
   Base query — cookies httpOnly enviados automaticamente
═══════════════════════════════════════════════════════════════ */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  credentials: "include",
});

/* ═══════════════════════════════════════════════════════════════
   Unwrapper do envelope
═══════════════════════════════════════════════════════════════ */
function unwrapEnvelope(result: {
  data?: unknown;
  error?: FetchBaseQueryError;
  meta?: Record<string, unknown>;
}):
  | { data: unknown; meta?: Record<string, unknown> }
  | { error: FetchBaseQueryError; meta?: Record<string, unknown> } {
  if (result.error) return { error: result.error, meta: result.meta };

  // 204 No Content
  const meta = result.meta as { response?: Response } | undefined;
  if (meta?.response?.status === 204) {
    return { data: undefined, meta: result.meta };
  }

  // Blob (PDF, Excel etc.) — retorna direto sem desempacotar
  if (result.data instanceof Blob) {
    return { data: result.data, meta: result.meta };
  }

  // Desempacotar ApiResponse<T>
  const body = result.data as ApiResponse<unknown>;

  if (body?.errors && body.errors.length > 0) {
    return {
      error: {
        status: body.statusCode,
        data: body.errors,
      } as FetchBaseQueryError,
      meta: result.meta,
    };
  }

  if (body?.statusCode >= 400) {
    return {
      error: {
        status: body.statusCode,
        data: [body.mensagemSucesso ?? `Erro ${body.statusCode}`],
      } as FetchBaseQueryError,
      meta: result.meta,
    };
  }

  return { data: body?.data, meta: result.meta };
}

/* ═══════════════════════════════════════════════════════════════
   Extrai erros do envelope dentro de error.data (para 401/403 etc.)
═══════════════════════════════════════════════════════════════ */
function extractEnvelopeError(result: {
  error?: FetchBaseQueryError;
}): { error: FetchBaseQueryError } | null {
  const envelope = result.error?.data as ApiResponse<unknown> | undefined;
  if (envelope?.errors && envelope.errors.length > 0) {
    return {
      error: {
        status: envelope.statusCode,
        data: envelope.errors,
      } as FetchBaseQueryError,
    };
  }
  return null;
}

/* ═══════════════════════════════════════════════════════════════
   Base query com refresh automático via cookie + unwrap

   O refresh token está em httpOnly cookie — o browser envia
   automaticamente. Não precisamos lê-lo no JavaScript.
═══════════════════════════════════════════════════════════════ */
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await refreshMutex.waitForUnlock();

  let result = await rawBaseQuery(args, api, extraOptions);

  // Rate limiting (429)
  if (result.error?.status === 429) {
    return {
      error: {
        status: 429,
        data: [
          "Muitas tentativas de acesso. Aguarde alguns instantes e tente novamente.",
        ],
      } as FetchBaseQueryError,
    };
  }

  // Permissão negada (403) — refaz /me para atualizar permissões no Redux
  if (result.error?.status === 403) {
    const meResult = await rawBaseQuery("/api/auth/me", api, extraOptions);
    const unwrappedMe = unwrapEnvelope(meResult);
    if ("data" in unwrappedMe && unwrappedMe.data) {
      api.dispatch({ type: "auth/setUser", payload: unwrappedMe.data });
    }
    // Repassa o erro 403 original para o chamador
    return extractEnvelopeError(result) ?? unwrapEnvelope(result);
  }

  if (result.error?.status === 401) {
    // Verifica se é um endpoint de auth (login, esqueci-senha, etc.)
    // Nesses casos, repassa o erro original sem tentar refresh
    const url = typeof args === "string" ? args : args.url;
    const isAuthEndpoint =
      url.includes("/api/auth/sign-in") ||
      url.includes("/api/auth/esqueci-senha") ||
      url.includes("/api/auth/redefinir-senha") ||
      url.includes("/api/auth/definir-senha") ||
      url.includes("/api/auth/verificar-mfa") ||
      url.includes("/api/auth/verificar-recovery-code") ||
      url.includes("/api/auth/refresh-token");

    if (isAuthEndpoint) {
      return extractEnvelopeError(result) ?? unwrapEnvelope(result);
    }

    // Tenta refresh — com mutex para evitar corrida
    if (!refreshMutex.isLocked()) {
      const release = await refreshMutex.acquire();
      try {
        const refreshResult = await rawBaseQuery(
          { url: "/api/auth/refresh-token", method: "POST" },
          api,
          extraOptions
        );

        const unwrapped = unwrapEnvelope(refreshResult);

        if (!("error" in unwrapped)) {
          // Refresh OK — novos cookies já foram setados pelo backend
          // Retry da request original
          result = await rawBaseQuery(args, api, extraOptions);
        } else {
          // Refresh falhou — logout
          api.dispatch({ type: "auth/clearUser" });
          return {
            error: {
              status: 401,
              data: ["Sessão expirada. Faça login novamente."],
            } as FetchBaseQueryError,
          };
        }
      } finally {
        release();
      }
    } else {
      // Outro refresh em andamento — espera e tenta de novo
      await refreshMutex.waitForUnlock();
      result = await rawBaseQuery(args, api, extraOptions);
    }
  }

  return unwrapEnvelope(result);
};

/* ═══════════════════════════════════════════════════════════════
   Base API — features injetam seus endpoints com injectEndpoints
═══════════════════════════════════════════════════════════════ */
export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "PagamentoCards",
    "PagamentoLotes",
    "PagamentoPeriodos",
    "AprovacaoCards",
    "AprovacaoLista",
    "AprovacaoPeriodos",
    "OrdemServico",
    // O ciclo de vida da OS avança o status do pedido de venda
    // (Confirmado → Em Instalação → Concluído → Entregue), então as mutations
    // de OS invalidam esta tag para revalidar a grade de Pedidos de Venda.
    "PedidoVenda",
    // Confirmar/recusar um recebimento avança o status do pedido de compra
    // (Enviado ao Fornecedor → Parcialmente Recebido → Recebido), então as
    // mutations de recebimento invalidam esta tag.
    "PedidoCompra",
    "NFe",
    "Nfse",
    "SupervisoresLookup",
    "DashboardFiscal", // F-010 US-006 / 194406
    "Fornecedores",
    "FornecedoresLookup",
    // F-016 US-001 — cadastro de Empresa
    "Empresas",
    "EmpresasLookup",
  ],
  endpoints: () => ({}),
});
